import { useDebouncedValue } from "@mantine/hooks";
import { useState } from "react";

import type { Address, EligibilityRequest } from "../../../types";

import { getEligibility } from "../../../api/eligibility";
import { useSalesContext } from "../../../sales-context/SalesContext";
import { isInternationalCaller } from "../../../shared-utils/formatters/validatePhoneNumber";
import { useIntegratedFlowStore, useProductOfferStore, useVerifiedCustomerStore } from "../../../stores";
import { EligibilityStatus } from "../../../types";
import { parsePhoneNumberAttributes } from "../utils/parsePhoneNumberAttributes";
import { useAddressSearch } from "./useAddressSearch";
import { useCollectCustomerInfoForm } from "./useCollectCustomerInfoForm";

export const useCollectCustomerInfo = () => {
    const {
        sessionId,
        logger: baseLogger,
        partner,
        analytics,
        taskType,
        taskData,
        apiClients,
        features,
    } = useSalesContext();
    const logger = baseLogger.child({ module: "useCollectCustomerInfo", supportTeam: "sales" });

    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const setCustomer = useVerifiedCustomerStore((state) => state.setCustomer);
    const integratedStep = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));
    const updateIntegratedStep = useIntegratedFlowStore((state) => state.updateIntegratedStep);
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));

    if (!customer || !customer.mdn) {
        logger.warn("Customer data is undefined");
        throw new Error("Customer data is undefined");
    }

    if (!taskType.isVoice && !taskType.isChat) {
        logger.warn("Active task is not voice or chat task");
        throw new Error("Active task is not voice or chat task");
    }

    const form = useCollectCustomerInfoForm(customer);
    const [debouncedAddressSearch] = useDebouncedValue(form.values.addressSearch, 300);
    const { data: addressSuggestions = [], isLoading } = useAddressSearch(debouncedAddressSearch);

    const [eligibilityStatus, setEligibilityStatus] = useState<EligibilityStatus | null>(null);
    const [isLoadingEligibility, setIsLoadingEligibility] = useState(false);
    const [sentIneligibleAddress, setSentIneligibleAddress] = useState(false);

    const handleContinueToPayment = async () => {
        try {
            setIsLoadingEligibility(true);

            const address: Address = {
                Address1: form.values.address1,
                City: form.values.city,
                StateProvinceCode: form.values.state,
                PostalCode: form.values.zip,
                CountryCode: form.values.countryCode,
            };

            setCustomer(sessionId, {
                ...customer,
                firstName: form.values.firstName,
                lastName: form.values.lastName,
                fullName: `${form.values.firstName} ${form.values.lastName}`,
                email: form.values.email,
                address,
            });

            const request: EligibilityRequest = {
                asurionCallId: taskType.isVoice ? taskData.asurionCallId : undefined,
                customerInteractionId: taskData.customerInteractionId,
                partner,
                reservationSid: taskData.reservationSid ?? sessionId,
                sessionId,
                ...parsePhoneNumberAttributes(customer?.mdn),
                address,
                customer: {
                    firstName: form.values.firstName,
                    lastName: form.values.lastName,
                    email: form.values.email,
                },
                eligibilityAttributes: {
                    currentPlan: customer?.productName,
                    // Same ANI fallback as useEligibility (nothing writes the metadata today).
                    isInternationalCaller:
                        customer?.metadata?.isInternationalCaller ?? isInternationalCaller(taskData.mdn),
                    chatSourceApp: taskType.isChat ? taskData.chatSourceApp : undefined,
                },
            };

            // getEligibility already writes the full product list to the store. Don't overwrite it
            // here: a primary-only write drops the session's tracking-only offers.
            const eligibilityResult = await getEligibility(
                apiClients.sales,
                logger,
                analytics,
                request,
                features.isUsingMovedDiscoveryAndEngagementQuestions,
            );
            const { productConfigs } = eligibilityResult;
            const selectedEligibleProduct = productConfigs.find((p) => p.productSku === selectedProduct?.productSku);

            if (!selectedEligibleProduct) {
                useProductOfferStore.getState().updateSelectedProduct(sessionId, undefined);
                updateIntegratedStep(sessionId, "SuggestedOfferStep");
            } else if (selectedEligibleProduct.status === EligibilityStatus.Eligible) {
                setEligibilityStatus(selectedEligibleProduct.status);
                useProductOfferStore.getState().updateSelectedProduct(sessionId, selectedEligibleProduct);
                updateIntegratedStep(sessionId, "SendLinkStep");
            } else {
                if (!sentIneligibleAddress) {
                    setSentIneligibleAddress(true);
                    void analytics.dispatcher.dispatchBusinessEvent("FirstIneligibleAddressReceived");
                } else {
                    setEligibilityStatus(selectedEligibleProduct.status);
                    useProductOfferStore.getState().updateSelectedProduct(sessionId, selectedEligibleProduct);
                    void analytics.dispatcher.dispatchBusinessEvent("SecondIneligibleAddressReceived");
                }
            }

            void analytics.dispatcher
                .withExtra({ nextStep: integratedStep })
                .dispatchBusinessEvent("ButtonClicked_ContinueToPayment");
        } catch (error) {
            logger.warn({ error }, "Error checking eligibility");
        } finally {
            setIsLoadingEligibility(false);
        }
    };

    return {
        addressSuggestions,
        customerMdn: customer.mdn,
        debouncedAddressSearch,
        eligibilityStatus,
        form,
        handleContinueToPayment,
        integratedStep,
        isLoading,
        isLoadingEligibility,
        product: selectedProduct,
        sentIneligibleAddress,
        setSentIneligibleAddress,
    };
};
